import React, { useEffect, useState } from 'react';

import { useModalActions } from '../../../layout/Modals/modalActions';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography/Typography';
import { useMutateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useMutateResource';
import { ModalNoticeUI } from '@cube3/ui/src/Modal/ModalNoticeUI';
import Button from '@cube3/cubicle/src/core/atoms/Button/Button';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { projectStatusTypes } from '../config/filters';
import { Checkbox } from '@cube3/cubicle/src/core/atoms/Checkbox/Checkbox';
import { VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { ProjectVisibilitySwitch } from '../Subcomponents/ProjectVisibilitySwitch';
import { Divider } from '@cube3/cubicle/src/core/atoms/Divider/Divider';

/**
 * @summary: Modal for changing project visibility, redirects user to ProjectSettingsModal
 */

const SET_PUBLIC = 'SET_PUBLIC';
const SET_PRIVATE = 'SET_PRIVATE';

const ARCHIVE = 'ARCHIVE';
const UNARCHIVE = 'UNARCHIVE';

export const changeProjectStatusActions = {
  ARCHIVE,
  UNARCHIVE
} as const;

export const changeProjectAccessActions = {
  SET_PUBLIC,
  SET_PRIVATE
} as const;

type Action = keyof typeof changeProjectStatusActions;

interface Props {
  modalContext: {
    action: Action;
    projectIds: string[];
    accessIds: string[];
    workspaceName: string;
    multiple?: boolean;
  };
}
const mutateConfig = { cacheInvalidator: null };
const ChangeProjectAccessModal: React.FunctionComponent<Props> = props => {
  const {
    action,
    projectIds,
    accessIds,
    workspaceName,
    multiple = false
  } = props.modalContext;
  const { previousModal } = useModalActions();

  const [mutate, mutateStatus] = useMutateResource__ALPHA(mutateConfig);
  const loading =
    mutateStatus &&
    mutateStatus !== statuses.SUCCESS &&
    mutateStatus !== statuses.FAILED;

  const [accessChange, setAccessChange] = useState<boolean | undefined>(action === ARCHIVE)

  const handleStatusChange = React.useCallback(() => {
    if (projectIds.length === 0) return;
    projectIds?.forEach(id => {
      mutate({
        id: id,
        type: 'project',
        is_public: accessIds.length === 0 ? undefined : accessChange,
        project_status: action === ARCHIVE ? projectStatusTypes.ARCHIVED : projectStatusTypes.ACTIVE
      });
    });
  }, [projectIds, action, mutate, accessChange]);

  useEffect(() => {
    if (mutateStatus && mutateStatus === statuses.SUCCESS) {
      previousModal();
    }
  }, [loading, mutateStatus, previousModal]);

  return (
    <ModalNoticeUI
      title={`${action === ARCHIVE ? "Archive" : 'Unarchive'} Project?`}
      loading={loading}
      onCloseEvent={previousModal}
      footerRightComponent={
        <>
          <Button
            label="Cancel"
            background="secondary"
            buttonStyle='outline'
            onClick={previousModal}
          />
          <Button
            label={action === ARCHIVE ? "Archive" : "Unarchive"}
            background="primary"
            buttonStyle='solid'
            onClick={handleStatusChange}
          />
        </>
      }
    >
      {action === ARCHIVE ? (
        <VBox gap={16}>
          <Typography variant="md" colorVariant='03'>
            Archiving a project makes it read only. Project contents cannot be modified, but a project can be Unarchived at a later time.
          </Typography>
          <Divider />

          <VBox gap={16}>


            <ProjectVisibilitySwitch multiple={multiple} isPublic={accessChange} setIsPublic={(setAccessChange)} workspaceName={workspaceName} />


          </VBox>

        </VBox>
      ) : (
        <VBox gap={16}>
          <Typography variant="md" colorVariant='03'>
            Unarchiving a project removes the read only status. Project contents can be modified afterwards.
          </Typography>
          <Divider />
          <VBox gap={16}>


            <ProjectVisibilitySwitch multiple={multiple} isPublic={accessChange} setIsPublic={(setAccessChange)} workspaceName={workspaceName} />

          </VBox>

        </VBox>
      )}
    </ModalNoticeUI>
  );
};

export default ChangeProjectAccessModal;
